import React, { useEffect, useMemo, useState } from "react";
import {
  BaseModalBody,
  BaseModalFooter,
  BaseModalHeader,
  DriverAvatar,
  Loading,
} from "@src/common/components";
import CourieButton from "@src/common/components/Button";
import {
  DispatchSource,
  Driver,
  OrderingAlgorithmType,
  Shipment,
  Stop,
  Task,
  TaskStatus,
  useGetDriverTasksByDriverIdLazyQuery,
  useReplaceRouteMutation,
} from "@api/graphql/generated/generated-types";
import { ApolloError } from "@apollo/client";
import { EditOrderShipment } from "./hooks/useEditOrderShipment";
import ChangeDriverRouteView from "@src/common/components/ChangeDriverRouteView/ChangeDriverRouteView";
import { getUniqueStopDates } from "@src/common/components/Widgets/DispatchCandidatesWidget/utils/assignDriverModalUtils";
import StopDateListTimeline from "@src/common/components/ChangeDriverRouteView/components/StopDateListTimeline";
import moment from "moment";
import { useAuth } from "@src/common/hooks/useAuth";
import { useErrorToast } from "@src/common/hooks/useErrorToast";
import { StopOrderHint, placeStopsInDispatcherOrder } from "@src/shipments/features/stopReordering";

interface Props {
  onConfirm: () => void;
  onCancel: () => void;
  shipment: EditOrderShipment;
  stopOrderHint?: StopOrderHint | null;
}

function ChangeRouteSlide({
  onConfirm,
  onCancel,
  shipment,
  stopOrderHint,
}: Props) {
  const { courierId, courieUser, employeeId } = useAuth();
  const showError = useErrorToast();
  const [routeDetails, setRouteDetails] =
    useState<Stop[] | undefined>(undefined);
  const [isValidOrder, setIsValidOrder] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [listStopDates, setListStopDates] = useState<string[]>([]);
  const [selectedStopDate, setSelectedStopDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOrderingAlgorithmType, setSelectedOrderingAlgorithmType] =
    useState<OrderingAlgorithmType | undefined>(OrderingAlgorithmType.Manual);
  const [autoRoutedStopIds, setAutoRoutedStopIds] =
    useState<undefined | string[]>(undefined);

  const isLastStopDate =
    listStopDates === undefined ||
    listStopDates.length === 0 ||
    listStopDates.indexOf(selectedStopDate) === listStopDates.length - 1;

  const [
    getDriverTasksByDriverId,
    {
      data: GetDriverTasksByDriverIdData,
      loading: GetDriverTasksByDriverIdLoading,
      error: GetDriverTasksByDriverIdError,
      refetch: GetDriverTasksByDriverIdRefetch,
    },
  ] = useGetDriverTasksByDriverIdLazyQuery({
    fetchPolicy: "network-only",
  });
  const [
    replaceRouteMutation,
    {
      data: ReplaceRouteMutationData,
      loading: ReplaceRouteMutationLoading,
      error: ReplaceRouteMutationError,
    },
  ] = useReplaceRouteMutation();

  const shipmentStopsMemo = useMemo(
    () => shipment?.stops,
    [JSON.stringify(shipment?.stops?.map((stop) => stop.stopDate))]
  );

  useEffect(() => {
    if (shipmentStopsMemo) {
      const orderStops = shipmentStopsMemo || [];
      const filteredStops = orderStops.filter(
        (stop) => stop.completedAt === null
      );
      const uniqueStopDates: undefined | string[] =
        getUniqueStopDates(filteredStops);
      if (uniqueStopDates && uniqueStopDates.length > 1) {
        setListStopDates(uniqueStopDates);
        setSelectedStopDate(uniqueStopDates[0]);
      } else {
        if (uniqueStopDates && uniqueStopDates.length === 1) {
          if (shipment?.shipmentDate !== uniqueStopDates[0]) {
            setSelectedStopDate(uniqueStopDates[0]);
            setListStopDates(uniqueStopDates);
            return;
          }
        }
        setListStopDates([]);
        setSelectedStopDate(shipment?.shipmentDate);
      }
    }
  }, [shipmentStopsMemo]);

  useEffect(() => {
    const driverId = shipment?.driver?.id;
    if (!driverId || !selectedStopDate || !courierId) return;

    getDriverTasksByDriverId({
      variables: {
        courierId: courierId,
        driverId: driverId,
        date: selectedStopDate,
        statusFilter: [TaskStatus.Created, TaskStatus.Started],
      },
    }).catch((error) => {
      showError(error as ApolloError);
    });
  }, [shipment, selectedStopDate]);

  useEffect(() => {
    if (GetDriverTasksByDriverIdLoading) {
      return;
    }
    if (GetDriverTasksByDriverIdData) {
      updateRouteDetails();
    }
  }, [GetDriverTasksByDriverIdData, GetDriverTasksByDriverIdLoading]);

  const onSubmit = () => {
    if (!shipment?.driver?.id || !routeDetails || !selectedStopDate || !courierId)
      return;

    const allDates = routeDetails.map(
      (stop) => stop.effectiveStopDate as string
    );
    const uniqueSortedDates = Array.from(new Set(allDates))
      .filter(Boolean)
      .sort((a, b) => moment(a).valueOf() - moment(b).valueOf());

    if (!uniqueSortedDates.includes(selectedStopDate)) return;

    const stopIds = routeDetails.map((stop) => stop.id);
    replaceRouteMutation({
      variables: {
        courierId: courierId,
        driverId: shipment.driver.id,
        newStopIds: stopIds,
        shipmentDate: selectedStopDate,
        replacedBy: employeeId ? employeeId : courieUser!.id,
        orderingAlgorithmType: selectedOrderingAlgorithmType,
        autoRoutedStopIds: autoRoutedStopIds ? autoRoutedStopIds : [],
        dispatchSource: DispatchSource.Dispatcher,
      },
    })
      .then(() => {
        if (isLastStopDate) {
          onConfirm && onConfirm();
        } else {
          const nextStopDateIndex = listStopDates.indexOf(selectedStopDate) + 1;
          setSelectedStopDate(listStopDates[nextStopDateIndex]);
        }
      })
      .catch((error) => {
        showError(error as ApolloError);
      });
  };

  const handleManualClick = () => {
    setSelectedOrderingAlgorithmType(OrderingAlgorithmType.Manual);
    if (GetDriverTasksByDriverIdData) {
      updateRouteDetails();
    }
  };

  const updateRouteDetails = () => {
    const tasks =
      (GetDriverTasksByDriverIdData?.driverById?.tasks as Task[]) || [];
    const sortedTasks = tasks.sort((a, b) => {
      return a.sequenceNumber - b.sequenceNumber;
    });
    const stops = sortedTasks.map((task) => task.stop);
    const routeStops = stopOrderHint
      ? placeStopsInDispatcherOrder({
          route: stops,
          shipment,
          stopOrderHint,
          selectedDate:
            listStopDates.length === 0 ? shipment.shipmentDate : selectedStopDate,
          isFirstTaskStarted: sortedTasks[0]?.status === TaskStatus.Started,
        })
      : stops;
    setRouteDetails(routeStops);
    const stopIds = routeStops.map((stop) => stop.id);
    setAutoRoutedStopIds(stopIds);
  };

  return (
    <>
      <BaseModalHeader>
        <div className="flex justify-between">
          <div className="flex gap-2">
            <DriverAvatar
              driverPhoto={shipment?.driver?.photoUrl || undefined}
              driverName={shipment?.driver?.firstName || undefined}
              avatarDisplay={shipment?.driver?.avatarDisplay || undefined}
              size={25}
            />
            Confirm Route ({shipment?.driver?.firstName})
          </div>
          <div className="">
            <StopDateListTimeline
              listStopDates={listStopDates}
              selectedStopDate={selectedStopDate}
              shipmentDate={shipment?.shipmentDate || ""}
            />
          </div>
          <span className="mr-3"></span>
        </div>
      </BaseModalHeader>
      <BaseModalBody
        style={{ minWidth: 1200, height: "70vh", overflow: "scroll" }}
      >
        <ChangeDriverRouteView
          loading={GetDriverTasksByDriverIdLoading}
          routeDetails={routeDetails}
          setRouteDetails={setRouteDetails}
          setIsValidOrder={setIsValidOrder}
          isValidOrder={isValidOrder}
          selectedShipment={shipment as unknown as Shipment}
          driver={shipment?.driver as Driver | undefined}
          setErrorMessage={setErrorMessage}
          selectedStopDate={
            listStopDates.length === 0 ? shipment?.shipmentDate : selectedStopDate
          }
          onClickManual={() => {
            handleManualClick();
          }}
          setLoading={setIsLoading}
          onAlgorithmTypeChange={setSelectedOrderingAlgorithmType}
          algorithmType={selectedOrderingAlgorithmType}
          setAutoRoutedStopIds={setAutoRoutedStopIds}
        />
      </BaseModalBody>
      <BaseModalFooter className="flex justify-between">
        {listStopDates && listStopDates.length > 1 ? (
          <CourieButton
            color={"secondary"}
            onClick={() => {
              const prevStopDateIndex =
                listStopDates.indexOf(selectedStopDate) - 1;
              if (prevStopDateIndex >= 0) {
                setSelectedStopDate(listStopDates[prevStopDateIndex]);
              } else {
              }
            }}
          >
            Back
          </CourieButton>
        ) : (
          <CourieButton color={"secondary"} onClick={onCancel}>
            Cancel
          </CourieButton>
        )}
        <Loading loading={ReplaceRouteMutationLoading} className="flex">
          {errorMessage && (
            <span className="text-red-600 my-auto pr-5">{errorMessage}</span>
          )}
          <CourieButton
            isProcessing={ReplaceRouteMutationLoading || isLoading}
            onClick={onSubmit}
            disabled={
              !isValidOrder ||
              isLoading ||
              ReplaceRouteMutationLoading ||
              GetDriverTasksByDriverIdLoading
            }
          >
            {isLastStopDate ? `Save` : "Save & Continue"}
          </CourieButton>
        </Loading>
      </BaseModalFooter>
    </>
  );
}

export default ChangeRouteSlide;
